import type { Edge } from "@xyflow/react";

import { useDipLabels } from "~/features/dataImportProjects/hooks/useDipLabels";
import type { DipFlowNode, DipSchemaGraph } from "~/features/dataImportProjects/types";
import { DipPipelineFlow } from "./DipPipelineFlow";

const EDGE_TYPE = "default" as const;
const EDGE_STYLE = { stroke: "#8DA5C8", strokeWidth: 1.5 } as const;
const MAIN_ID = "schema-main";
const REL_X = 300;
const ENT_X = 600;
const ROW_GAP = 104;

interface DipSchemaFlowProps {
  graph: DipSchemaGraph;
}

function buildSchemaFlow(graph: DipSchemaGraph): { nodes: DipFlowNode[]; edges: Edge[] } {
  const rows = Math.max(graph.pairs.length, 1);
  const midY = ((rows - 1) * ROW_GAP) / 2;
  const nodes: DipFlowNode[] = [
    {
      id: MAIN_ID,
      type: "dipSchema",
      position: { x: 0, y: midY },
      data: {
        title: graph.mainTitle ?? "—",
        kind: "main",
        category: graph.mainCategory,
        detailPath: graph.mainDetailPath,
      },
      draggable: false,
      selectable: false,
    },
  ];
  const edges: Edge[] = [];
  graph.pairs.forEach((pair, i) => {
    const y = i * ROW_GAP;
    nodes.push({
      id: pair.relationId,
      type: "dipSchema",
      position: { x: REL_X, y },
      data: {
        title: pair.relationTitle,
        kind: "relation",
        category: pair.relationCategory,
        detailPath: pair.relationDetailPath,
      },
      draggable: false,
      selectable: false,
    });
    nodes.push({
      id: pair.entityId,
      type: "dipSchema",
      position: { x: ENT_X, y },
      data: {
        title: pair.entityTitle,
        kind: "entity",
        category: pair.entityCategory,
        detailPath: pair.entityDetailPath,
      },
      draggable: false,
      selectable: false,
    });
    edges.push({
      id: `${MAIN_ID}->${pair.relationId}`,
      source: MAIN_ID,
      target: pair.relationId,
      type: EDGE_TYPE,
      style: EDGE_STYLE,
    });
    edges.push({
      id: `${pair.relationId}->${pair.entityId}`,
      source: pair.relationId,
      target: pair.entityId,
      type: EDGE_TYPE,
      style: EDGE_STYLE,
    });
  });
  return { nodes, edges };
}

export function DipSchemaFlow({ graph }: DipSchemaFlowProps) {
  const labels = useDipLabels();
  const { nodes, edges } = buildSchemaFlow(graph);
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-16-bold">{labels["schema.title"]}</h3>
      </div>
      <DipPipelineFlow nodes={nodes} edges={edges} heightClass="h-[360px]" />
    </section>
  );
}
